import { useFormContext } from 'react-hook-form';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription
} from '@components/ui/form';
import { Textarea } from '@components/ui/textarea';
import { Checkbox } from '@components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@components/ui/select';

const howDidYouHearOptions = [
  'Website',
  'Social Media',
  'Email',
  'Referral',
  'Attended an Atlantic Group Event',
  'E-Newsletter',
  'Repeat Client',
  'Google',
  'Article/Publication',
  'Word of Mouth',
  'Attended an Event',
  'Other'
];

const socialMediaPlatformOptions = [
  'Instagram',
  'Facebook',
  'LinkedIn',
  'TikTok'
];

interface AdditionalDetailsSectionProps {
  enquiryType: string;
}

export default function AdditionalDetailsSection({ enquiryType }: AdditionalDetailsSectionProps) {
  const form = useFormContext();
  const isEvent = enquiryType === 'Event Enquiry';
  const watchHowDidYouHear = form.watch('howDidYouHear');
  
  const stepNumber = isEvent ? '04' : '03';
  
  return (
    <section className="pb-12 mb-12">
      <div className="text-center mb-8">
        <span className="text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground block mb-3">
          Step {stepNumber}
        </span>
        <h2 className="text-[28px] md:text-[32px] font-bold text-foreground uppercase tracking-[0.025em] mb-3">
          Additional Details
        </h2>
        <p className="text-sm text-muted-foreground">
          Help us understand your needs better
        </p>
      </div>
      
      <div className="space-y-6">
        {isEvent && (
          <FormField
            control={form.control}
            name="tellUsMore"
            render={({ field }) => (
              <FormItem data-field={field.name}>
                <FormLabel className="text-sm font-semibold text-foreground">
                  Tell Us More
                </FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Any special requirements, preferences, or questions..."
                    rows={6}
                    {...field}
                    className="text-sm border-border focus:border-ring focus:shadow-[0_0_0_3px_rgba(33,33,33,0.08)] resize-none"
                  />
                </FormControl>
                <FormDescription className="text-sm text-muted-foreground mt-1">
                  Share any additional details about your event
                </FormDescription>
                <FormMessage className="text-[13px]" />
              </FormItem>
            )}
          />
        )}
        
        <FormField
          control={form.control}
          name="howDidYouHear"
          rules={isEvent ? { required: 'This field is required' } : undefined}
          render={({ field }) => (
            <FormItem data-field={field.name}>
              <FormLabel className="text-sm font-semibold text-foreground">
                How did you hear about us? {isEvent && <span className="text-destructive">*</span>}
              </FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger className="h-11 text-sm border-border focus:border-ring focus:shadow-[0_0_0_3px_rgba(33,33,33,0.08)]">
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {howDidYouHearOptions.map((option) => (
                    <SelectItem key={option} value={option} className="text-sm">
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage className="text-[13px]" />
            </FormItem>
          )}
        />
        
        {watchHowDidYouHear === 'Social Media' && (
          <FormField
            control={form.control}
            name="socialMediaPlatform"
            render={({ field }) => (
              <FormItem data-field={field.name}>
                <FormLabel className="text-sm font-semibold text-foreground">
                  Which social media platform?
                </FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className="h-11 text-sm border-border focus:border-ring focus:shadow-[0_0_0_3px_rgba(33,33,33,0.08)]">
                      <SelectValue placeholder="Select platform" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {socialMediaPlatformOptions.map((option) => (
                      <SelectItem key={option} value={option} className="text-sm">
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage className="text-[13px]" />
              </FormItem>
            )}
          />
        )}
        
        <FormField
          control={form.control}
          name="agreeToPrivacy"
          rules={{ required: 'You must agree to the privacy disclaimer to continue' }}
          render={({ field }) => (
            <FormItem className="bg-muted/50 p-6 rounded-[4px]">
              <div className="flex items-start space-x-3">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none flex-1">
                  <FormLabel className="text-sm font-semibold text-foreground">
                    I agree to the privacy disclaimer <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormDescription className="text-sm text-muted-foreground leading-relaxed mt-2">
                    By submitting this form, you agree to Atlantic Group collecting and storing your personal information in accordance with our{' '}
                    <a
                      href="https://atlanticgroup.com.au/privacy-policy/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline font-semibold"
                    >
                      privacy policy
                    </a>
                  </FormDescription>
                </div>
              </div>
              <FormMessage className="text-[13px] ml-7" />
            </FormItem>
          )}
        />
      </div>
    </section>
  );
}
